/**
 * Formats a date from various input types to a readable string
 * @param {number|Object|Date} dateInput - Can be:
 *   - Firestore timestamp object {seconds: number, nanoseconds: number}
 *   - Epoch timestamp in milliseconds or seconds
 *   - JavaScript Date object
 * @param {Object} [options] - Formatting options
 * @param {string} [options.format='long'] - 'long' (January 1, 2023) or 'short' (01/01/2023)
 * @returns {string} Formatted date string
 */
export const formatDate = (dateInput, options = {}) => {
  if (!dateInput) return '';
  
  let date;
  
  // Handle Firestore timestamp
  if (dateInput.seconds) {
    date = new Date(dateInput.seconds * 1000);
  } 
  // Handle epoch time (check if it's in seconds or milliseconds)
  else if (typeof dateInput === 'number') {
    date = dateInput > 9999999999 ? new Date(dateInput) : new Date(dateInput * 1000);
  } 
  // Handle Date object or string that can be parsed to Date
  else {
    date = new Date(dateInput);
  }
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    console.warn('Invalid date provided to formatDate:', dateInput);
    return '';
  }
  
  const { format = 'long' } = options;
  
  if (format === 'short') {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }
  
  // Default long format
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Checks if a value is an epoch timestamp (in seconds or milliseconds)
 * @param {*} value - Value to check
 * @returns {boolean}
 */
export const isEpochTime = (value) => {
  if (typeof value !== 'number') return false;
  // If it's a number, check if it's a reasonable timestamp
  // (between 2000 and 2100)
  const minEpoch = new Date('2000-01-01').getTime();
  const maxEpoch = new Date('2100-01-01').getTime();
  
  // Handle both seconds and milliseconds
  return (
    (value > minEpoch / 1000 && value < maxEpoch / 1000) || // seconds
    (value > minEpoch && value < maxEpoch) // milliseconds
  );
};
